import React, { useState, useEffect } from 'react';
import DynamicSection from '../components/dynamic/DynamicSection';
import { timelineAPI } from '../services/api';
import { TimelineEvent } from '../types';
import { 
  FaBriefcase, 
  FaGraduationCap, 
  FaTrophy, 
  FaCode,
  FaCertificate,
  FaCircle
} from 'react-icons/fa';

const iconMap: { [key: string]: any } = {
  FaBriefcase,
  FaGraduationCap,
  FaTrophy,
  FaCode,
  FaCertificate,
};

const colorMap: { [key: string]: string } = {
  blue: 'bg-blue-500',
  green: 'bg-green-500',
  gold: 'bg-yellow-500',
  purple: 'bg-purple-500',
  red: 'bg-red-500',
};

const Timeline: React.FC = () => {
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const loadEvents = async () => {
      try {
        const response = await timelineAPI.getAll();
        if (response && response.data) {
          // Sort events by date (newest first)
          const sorted = Array.isArray(response.data)
            ? response.data.sort((a, b) => 
                new Date(b.date).getTime() - new Date(a.date).getTime()
              )
            : [];
          setEvents(sorted);
        } else {
          setEvents([]);
        }
      } catch (error: any) {
        console.error('Error loading timeline:', error);
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };
    loadEvents();
  }, []);

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center bg-white dark:bg-gray-900">
        <div className="text-xl text-gray-600 dark:text-gray-400">Loading timeline...</div>
      </div>
    );
  }

  return (
    <div className="pt-20 bg-white dark:bg-gray-900 min-h-screen">
      <DynamicSection
        title="Career Timeline"
        subtitle="My professional journey and key milestones"
      >
        <div className="max-w-4xl mx-auto">
          <div className="relative">
            {/* Timeline line */}
            {events.length > 0 && (
              <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-300 dark:bg-gray-700"></div>
            )}
            
            {events.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-600 dark:text-gray-400">No timeline events found.</p>
              </div>
            ) : (
              events.map((event, index) => {
              const IconComponent = iconMap[event.icon || 'FaCircle'] || FaCircle;
              const color = colorMap[event.color || 'blue'] || 'bg-blue-500';
              
              return (
                <div key={event.id} className="relative flex items-start mb-8">
                  {/* Icon */}
                  <div className={`relative z-10 flex items-center justify-center w-16 h-16 rounded-full ${color} text-white shadow-lg`}>
                    <IconComponent className="w-8 h-8" />
                  </div>
                  
                  {/* Content */}
                  <div className="ml-6 flex-grow">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                          {event.title}
                        </h3>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {new Date(event.date).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'long' 
                          })}
                        </span>
                      </div>
                      <p className="text-gray-700 dark:text-gray-300">
                        {event.description}
                      </p>
                      <span className={`inline-block mt-3 px-3 py-1 rounded-full text-xs font-semibold ${
                        event.type === 'experience' ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200' :
                        event.type === 'education' ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' :
                        event.type === 'achievement' ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200' :
                        'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                      }`}>
                        {event.type}
                      </span>
                    </div>
                  </div>
                </div>
              );
              })
            )}
          </div>
        </div>
      </DynamicSection>
    </div>
  );
};

export default Timeline;
